import React, { useState, useEffect } from 'react';
import { X, Send, HelpCircle, Mail } from 'react-feather';
import AuthService from '../../services/authService';
import darkIcon from '../../assets/dark-iconp.png';

const SupportChat = ({ isOpen, onClose }) => {
  // Detect light mode from parent container
  const [isLightMode, setIsLightMode] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedAction, setSelectedAction] = useState('general'); // Default: chat mode active
  const [selectedBillingOption, setSelectedBillingOption] = useState(null);
  const [selectedUserHelpOption, setSelectedUserHelpOption] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [returnReason, setReturnReason] = useState(null);
  const [contactConfirmations, setContactConfirmations] = useState({
    inApp: false,
    phone: false,
    email: false
  });
  const [contactedInApp, setContactedInApp] = useState(false);
  const [contactedViaPhone, setContactedViaPhone] = useState(false);
  const [contactedViaEmail, setContactedViaEmail] = useState(false);
  const [pauseDuration, setPauseDuration] = useState(null);
  const [renewalDay, setRenewalDay] = useState(null);
  // Account pause state — used to disable Pause / Change Renewal billing options
  // (and show a tooltip) when the agent is already on a pause.
  const [accountPaused, setAccountPaused] = useState(false);
  const [pauseEndsAt, setPauseEndsAt] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // ===== Chatbot state (General category only) =====
  const [isChatMode, setIsChatMode] = useState(true);    // true when user is live-chatting with the bot (default)
  const [chatHistory, setChatHistory] = useState([]);      // [{role:'user'|'assistant', content:string}] for OpenAI context
  const [isBotTyping, setIsBotTyping] = useState(false);   // shows "..." indicator while bot is responding
  const [unhelpfulCount, setUnhelpfulCount] = useState(0); // tracks detected "this didn't help" messages
  const [showCategoriesPopover, setShowCategoriesPopover] = useState(false); // controls Categories popover visibility
  const [showCloseConfirm, setShowCloseConfirm] = useState(false); // controls close-confirmation overlay
  const [showCancelConfirmed, setShowCancelConfirmed] = useState(false); // terminal overlay after a successful account cancellation
  const [showPauseConfirmed, setShowPauseConfirmed] = useState(false); // dismissable success overlay after a successful pause
  const [pauseRenewalDate, setPauseRenewalDate] = useState(null); // computed next-renewal date shown on the pause success overlay
  // Escalation flow state — when user clicks "Email to team" we snapshot the transcript
  // and walk through a mini Y/N flow before actually firing off the support email.
  const [escalationStage, setEscalationStage] = useState(null); // null | 'asking' | 'adding-note' | 'sending' | 'sent'
  const [capturedTranscript, setCapturedTranscript] = useState(''); // snapshot of chat at moment of escalation
  const transcriptFiredRef = React.useRef(false); // one-shot guard to prevent duplicate transcript webhooks per session
  const idleTimerRef = React.useRef(null); // 120s idle timer handle
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Hi 👋 I\'m the Lauft support assistant. Choose a category below for common workflows, or just chat with me for anything else.',
    }
  ]);
  const chatAreaRef = React.useRef(null);
  
  useEffect(() => {
    // Check if parent has light-mode class
    const checkLightMode = () => {
      const dashboardContainer = document.querySelector('.dashboard-container');
      setIsLightMode(dashboardContainer?.classList.contains('light-mode'));
    };
    
    checkLightMode();
    // Set up observer to detect theme changes
    const observer = new MutationObserver(checkLightMode);
    const dashboardContainer = document.querySelector('.dashboard-container');
    if (dashboardContainer) {
      observer.observe(dashboardContainer, { attributes: true, attributeFilter: ['class'] });
    }
    
    return () => observer.disconnect();
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTo({
        top: chatAreaRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, selectedBillingOption, pauseDuration]);

  // On open, pull the agent's current pause state so we can disable the Pause /
  // Change Renewal billing options while a pause is active. Non-blocking: if the
  // fetch fails we leave the options enabled rather than lock the user out.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          'https://back-end-x7tx.onrender.com/api/users/profile',
          { headers: { Authorization: `Bearer ${AuthService.getToken()}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data.success || !data.data) return;
        setAccountPaused((data.data.agentStatus || null) === 'paused');
        setPauseEndsAt(data.data.pauseEndsAt || null);
      } catch (e) {
        // Leave options enabled if we can't determine status.
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const handleQuickAction = (action) => {
    // Don't do anything if this action is already selected
    if (selectedAction === action) return;
    
    setSelectedAction(action);
    // Exit chat mode when switching to a structured category
    if (action !== 'general') {
      setIsChatMode(false);
      setChatHistory([]);
      setUnhelpfulCount(0);
    }
    
    // Different messages based on action type
    if (action === 'general') {
      // Enter live-chat mode with the Lauft support bot
      setIsChatMode(true);
      setChatHistory([]);
      setUnhelpfulCount(0);
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 I\'m the Lauft support assistant. Choose a category below for common workflows, or just chat with me for anything else.',
        }
      ]);
    } else if (action === 'call') {
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'We\'d love to chat! Please let us know what you\'d like to discuss and when you\'re available, and we\'ll send you a calendar link.',
        }
      ]);
    } else if (action === 'billing') {
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with your account?',
        }
      ]);
    } else if (action === 'feature') {
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'We\'re always looking to improve! Type your feature idea in the text box below ⬇️ and hit send — we\'ll share it with our product team.',
        }
      ]);
    } else if (action === 'bug') {
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'Sorry you\'re experiencing an issue! Type a description of what happened in the text box below ⬇️ and hit send — we\'ll investigate right away.',
        }
      ]);
    } else if (action === 'userhelp') {
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with a user?',
        }
      ]);
    }
  };

  // Tooltip shown on the disabled Pause / Change Renewal options while a pause
  // is active. Undefined (no tooltip) when the account isn't paused.
  const pausedOptionTooltip = accountPaused
    ? `Your account is already on a pause${pauseEndsAt ? ` until ${new Date(pauseEndsAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}` : ''}.`
    : undefined;

  const handleBillingOption = (option) => {
    // If already on a pause, the pause + change-renewal options are unavailable
    // (the buttons are disabled in the UI too — this is the belt-and-suspenders
    // guard). Cancel is still allowed.
    if (accountPaused && (option === 'pause' || option === 'change-renewal')) {
      return;
    }

    // Reset pause duration and renewal day when changing billing option
    if (option !== 'pause') {
      setPauseDuration(null);
    }
    if (option !== 'change-renewal') {
      setRenewalDay(null);
    }
    
    // Just select the option, don't submit yet
    setSelectedBillingOption(option);
    
    // If they selected "pause", show duration selection message
    if (option === 'pause') {
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with your account?',
        },
        {
          type: 'bot',
          text: 'How long would you like to pause your account?',
        }
      ]);
    } else if (option === 'cancel') {
      // Show cancel warning
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with your account?',
        },
        {
          type: 'bot',
          text: '⚠️ WARNING: Canceling your account will result in:\n\n• Loss of access to all claimed users\n• Loss of access to the agent portal\n• All current points will become unusable',
        },
        {
          type: 'bot',
          text: 'If you would like to retain access to your portal, consider pausing your account for up to 60 days instead. You\'ll keep full access to all features, including claiming new users with your current point balance, without billing or point reloads during the pause.',
        }
      ]);
    } else if (option === 'change-renewal') {
      // Show renewal day selection
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with your account?',
        },
        {
          type: 'bot',
          text: 'What day of the month would you like your renewal to occur? (1-31)',
        }
      ]);
    } else {
      // For other options, just keep the first two messages
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with your account?',
        }
      ]);
    }
  };

  const handleUserHelpOption = async (option) => {
    setSelectedUserHelpOption(option);
    
    // Fetch the agent's assigned users
    setLoadingUsers(true);
    try {
      const currentUser = AuthService.getCurrentUser();
      console.log('🔍 Fetching users for agent:', currentUser._id);
      
      const response = await fetch(`https://back-end-x7tx.onrender.com/api/users/agent/${currentUser._id}/buyers`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      
      console.log('🔍 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Response data:', data);
        console.log('🔍 Users array:', data.data);
        setAssignedUsers(data.data || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to fetch assigned users:', response.status, errorData);
        setAssignedUsers([]);
      }
    } catch (error) {
      console.error('❌ Error fetching assigned users:', error);
      setAssignedUsers([]);
    } finally {
      setLoadingUsers(false);
    }
    
    // Show appropriate message for the selected option
    if (option === 'return') {
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with a user?',
        },
        {
          type: 'bot',
          text: 'Please select the user you\'d like to return:',
        }
      ]);
    } else if (option === 'reassign') {
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with a user?',
        },
        {
          type: 'bot',
          text: 'Please select the user you\'d like to reassign:',
        }
      ]);
    } else if (option === 'report') {
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with a user?',
        },
        {
          type: 'bot',
          text: 'Please select the user you\'d like to report:',
        }
      ]);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    
    // Different flow based on the option
    if (selectedUserHelpOption === 'return') {
      // Return flow: Show reason selection
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with a user?',
        },
        {
          type: 'bot',
          text: `Please select the user you\'d like to return:`,
        },
        {
          type: 'bot',
          text: `You selected: ${user.name} (${user.email})`,
        },
        {
          type: 'bot',
          text: 'Why are you returning this user?',
        }
      ]);
    } else {
      // Reassign or Report: Just show text input prompt
      const actionText = selectedUserHelpOption === 'reassign' ? 'reassign' : 'report';
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with a user?',
        },
        {
          type: 'bot',
          text: `Please select the user you\'d like to ${actionText}:`,
        },
        {
          type: 'bot',
          text: `You selected: ${user.name} (${user.email})\n\nPlease provide additional details below:`,
        }
      ]);
    }
  };

  const handleReturnReason = (reason) => {
    setReturnReason(reason);
    
    if (reason === 'unresponsive') {
      // Show contact confirmation checkboxes
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with a user?',
        },
        {
          type: 'bot',
          text: `Please select the user you'd like to return:`,
        },
        {
          type: 'bot',
          text: `You selected: ${selectedUser.name} (${selectedUser.email})`,
        },
        {
          type: 'bot',
          text: 'Why are you returning this user?',
        },
        {
          type: 'bot',
          text: '⚠️ Please confirm you have contacted them a minimum of 3 times through the following methods:',
        }
      ]);
    } else {
      // For "cannot service" or "other", show text input
      const reasonText = reason === 'cannot_service' ? 'you cannot service this user' : 'you are returning this user';
      setMessages([
        {
          type: 'bot',
          text: 'Hi 👋 How can we help you today?',
        },
        {
          type: 'bot',
          text: 'What would you like to do with a user?',
        },
        {
          type: 'bot',
          text: `Please select the user you'd like to return:`,
        },
        {
          type: 'bot',
          text: `You selected: ${selectedUser.name} (${selectedUser.email})`,
        },
        {
          type: 'bot',
          text: 'Why are you returning this user?',
        },
        {
          type: 'bot',
          text: `Please explain why ${reasonText}:`,
        }
      ]);
    }
  };

  const handleContactConfirmation = (method) => {
    setContactConfirmations(prev => ({
      ...prev,
      [method]: !prev[method]
    }));
  };

  const handlePauseDuration = (duration) => {
    setPauseDuration(duration);
    
    // Show the strong auto-renewal warning with benefits (split into multiple bubbles)
    setMessages([
      {
        type: 'bot',
        text: 'Hi 👋 How can we help you today?',
      },
      {
        type: 'bot',
        text: 'What would you like to do with your account?',
      },
      {
        type: 'bot',
        text: 'How long would you like to pause your account?',
      },
      {
        type: 'bot',
        text: '⚠️ IMPORTANT: Your account will automatically resume at the end of this pause period. Billing, points accumulation, and all account features will resume on the renewal date.',
      },
      {
        type: 'bot', 
        text: 'During your pause, you will still be able to:\n\n• Message existing users\n• Send properties to existing users\n• View user behavior and analytics\n• See all available users nearby\n• Use existing points to claim users',
      },
      {
        type: 'bot',
        text: 'Billing and automatic point reloads will not occur during this time.',
      }
    ]);
  };

  const handleRenewalDay = (day) => {
    setRenewalDay(day);
    
    // Show confirmation message
    setMessages([
      {
        type: 'bot',
        text: 'Hi 👋 How can we help you today?',
      },
      {
        type: 'bot',
        text: 'What would you like to do with your account?',
      },
      {
        type: 'bot',
        text: 'What day of the month would you like your renewal to occur? (1-31)',
      },
      {
        type: 'bot',
        text: `Great! Click submit below to change your renewal date to day ${day} of the month.`,
      }
    ]);
  };

  // Log the (now-cancelled) agent out of the portal. Uses AuthService.logout()
  // when available, with a localStorage clear as a fallback, then hard-redirects
  // to the root so React auth state fully resets and the route guard bounces
  // them to login. If your login route isn't '/', change the redirect below.
  const handleCancelLogout = () => {
    try {
      if (typeof AuthService.logout === 'function') {
        AuthService.logout();
      }
    } catch (err) {
      console.error('Logout error:', err);
    }
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
    } catch (err) {
      /* no-op */
    }
    window.location.href = '/';
  };

  const handleBillingSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBillingOption) return;
    
    // For pause requests, require duration selection
    if (selectedBillingOption === 'pause' && !pauseDuration) return;
    
    // For change-renewal requests, require day selection
    if (selectedBillingOption === 'change-renewal' && !renewalDay) return;
    
    // Prevent double submission
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Send to backend (existing flow)
      console.log('Submitting billing request:', {
        billingOption: selectedBillingOption,
        pauseDuration: pauseDuration,
        renewalDay: renewalDay
      });
      
      const response = await fetch('https://back-end-x7tx.onrender.com/api/support/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          message: selectedBillingOption === 'pause' 
            ? `Billing request: pause for ${pauseDuration} days`
            : selectedBillingOption === 'change-renewal'
            ? `Billing request: change renewal date to day ${renewalDay}`
            : `Billing request: ${selectedBillingOption}`,
          category: 'billing',
          billingOption: selectedBillingOption,
          pauseDuration: pauseDuration, // Include pause duration if applicable
          renewalDay: renewalDay // Include renewal day if applicable
        })
      });
      
      console.log('Backend response status:', response.status, 'OK:', response.ok);
      
      if (response.ok) {
        console.log('Success! Showing confirmation message');
        // Show success confirmation based on action
        if (selectedBillingOption === 'pause') {
          setMessages([
            {
              type: 'bot',
              text: '✅ Your account has been successfully paused!',
            },
            {
              type: 'bot',
              text: `Your account is paused for ${pauseDuration} days. We've sent a confirmation email with all the details.`,
            },
            {
              type: 'bot',
              text: 'Your account will automatically resume after this period. You can still access all features except billing and automatic point reloads.',
            }
          ]);
          // Compute the next renewal / resume date (today + duration) — matches
          // how the backend sets pauseEndsAt — and show the success overlay.
          setPauseRenewalDate(new Date(Date.now() + pauseDuration * 24 * 60 * 60 * 1000));
          setShowPauseConfirmed(true);
        } else if (selectedBillingOption === 'cancel') {
          setMessages([
            {
              type: 'bot',
              text: '✅ Your cancellation request has been submitted.',
            },
            {
              type: 'bot',
              text: 'We\'ve received your account cancellation submission. Your account will be cancelled and you will receive a confirmation email shortly. Please always feel free to reach out with any questions!',
            },
            {
              type: 'bot',
              text: 'If you have any questions, please reply to the confirmation email.',
            }
          ]);
          // Account is now deactivated + unpooled server-side. Show the terminal
          // confirmation overlay with a logout action so they don't keep acting
          // in a dead session.
          setShowCancelConfirmed(true);
        } else if (selectedBillingOption === 'change-renewal') {
          setMessages([
            {
              type: 'bot',
              text: '✅ Your renewal date change request has been submitted!',
            },
            {
              type: 'bot',
              text: `We've received your request to change your renewal date to day ${renewalDay} of the month. You will receive a confirmation email shortly, and always feel free to reach back out with any questions.`,
            }
          ]);
        } else {
          setMessages([
            {
              type: 'bot',
              text: '✅ Your request has been submitted!',
            },
            {
              type: 'bot',
              text: 'We\'ll get back to you shortly at your registered email address.',
            }
          ]);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Backend error:', response.status, errorData);
        setMessages([
          {
            type: 'bot',
            text: '❌ Error',
          },
          {
            type: 'bot',
            text: 'Sorry, there was an error submitting your request. Please email us directly at hello@lauft.app',
          }
        ]);
      }
    } catch (error) {
      console.error('Error submitting billing request:', error);
      setMessages([
        {
          type: 'bot',
          text: '❌ Error',
        },
        {
          type: 'bot',
          text: 'Sorry, there was an error submitting your request. Please email us directly at hello@lauft.app',
        }
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ===== Chatbot: detect unhelpful user messages via common phrases =====
  const UNHELPFUL_PATTERNS = /\b(didn'?t help|not helpful|doesn'?t help|still confused|that'?s not what|not what i asked|you'?re not helping|useless|this isn'?t helping|wrong answer|incorrect|still don'?t understand|not an answer|still stuck)\b/i;
  const isUnhelpfulMessage = (text) => UNHELPFUL_PATTERNS.test(text || '');

  // ===== Chatbot: call the Lauft support bot via chat.lauft.app =====
  const CHAT_API_URL = 'https://chat.lauft.app/api/support-chat';
  const CHAT_API_SECRET = process.env.REACT_APP_SUPPORT_CHAT_SECRET || '';

  const sendToSupportBot = async (userMessage) => {
    setIsBotTyping(true);
    try {
      const response = await fetch(CHAT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Support-Secret': CHAT_API_SECRET,
        },
        body: JSON.stringify({
          message: userMessage,
          history: chatHistory, // most recent 10 already enforced server-side
        })
      });
      const data = await response.json();
      if (!response.ok || !data.reply) {
        return { error: data.error || 'Support chat temporarily unavailable.' };
      }
      return { reply: data.reply };
    } catch (err) {
      console.error('Support bot error:', err);
      return { error: 'Could not reach support chat.' };
    } finally {
      setIsBotTyping(false);
    }
  };

  // ===== Chatbot: escalate to team by pre-filling the standard ticket form with the chat transcript =====
  const handleSendToTeam = () => {
    // Snapshot the transcript at the moment of escalation — downstream prompts
    // ("want to add a note?") should NOT end up in the email body.
    const transcript = messages
      .filter(m => m.type === 'user' || m.type === 'bot')
      .map(m => `${m.type === 'user' ? 'User' : 'Bot'}: ${m.text}`)
      .join('\n');

    setCapturedTranscript(transcript);
    setEscalationStage('asking');
    setMessages(prev => [
      ...prev,
      { type: 'bot', text: 'Got it — I\'ll email this full transcript to our team. Want to add a note before I send it?' }
    ]);
  };

  // Fire the email to /api/support/contact with the captured transcript + optional note
  const sendEscalationEmail = async (note = '') => {
    setEscalationStage('sending');

    const body = note
      ? `Escalated from support chat.\n\n--- USER NOTE ---\n${note}\n\n--- TRANSCRIPT ---\n${capturedTranscript}`
      : `Escalated from support chat. No additional note added.\n\n--- TRANSCRIPT ---\n${capturedTranscript}`;

    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/support/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          message: body,
          category: 'general',
          userHelpOption: null,
          selectedUserId: null,
          selectedUserName: null,
          selectedUserEmail: null,
          returnReason: null,
          contactConfirmations: null,
        })
      });

      if (response.ok) {
        setEscalationStage('sent');
        setMessage('');
        setMessages(prev => [
          ...prev,
          { type: 'bot', text: 'Sent! Our team will reach out to your registered email shortly.' }
        ]);
      } else {
        setEscalationStage('asking'); // let them retry
        setMessages(prev => [
          ...prev,
          { type: 'bot', text: 'Hmm, something went wrong sending that. Try again?' }
        ]);
      }
    } catch (err) {
      console.error('Escalation send error:', err);
      setEscalationStage('asking');
      setMessages(prev => [
        ...prev,
        { type: 'bot', text: 'Couldn\'t reach our server. Try again in a moment?' }
      ]);
    }
  };

  const handleEscalationAddNote = () => {
    setEscalationStage('adding-note');
    setMessages(prev => [
      ...prev,
      { type: 'bot', text: 'Go ahead — type your note below and I\'ll include it with the transcript.' }
    ]);
  };

  const handleEscalationSendNow = () => {
    // Fire immediately with no note
    sendEscalationEmail('');
  };

  // ===== Transcript logging webhook =====
  // Fire a silent webhook with the chat transcript when the session ends
  // (user closes modal) or goes idle for 120s. One-shot per session, no UI feedback.
  const fireTranscriptWebhook = async () => {
    if (transcriptFiredRef.current) return; // one-shot guard
    // Skip empty/welcome-only sessions — nothing useful to capture
    if (messages.length <= 1 && chatHistory.length === 0) return;
    transcriptFiredRef.current = true;

    const transcript = messages
      .filter(m => m.type === 'user' || m.type === 'bot')
      .map(m => `${m.type === 'user' ? 'User' : 'Bot'}: ${m.text}`)
      .join('\n');

    const body = `Support chat transcript (auto-logged on close/idle).\n\n--- TRANSCRIPT ---\n${transcript}`;

    try {
      await fetch('https://back-end-x7tx.onrender.com/api/support/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          message: body,
          category: 'transcript',
          userHelpOption: null,
          selectedUserId: null,
          selectedUserName: null,
          selectedUserEmail: null,
          returnReason: null,
          contactConfirmations: null,
        })
      });
    } catch (err) {
      // Silent — no user notification needed for analytics logging
      console.error('Transcript webhook error:', err);
    }
  };

  // Reset the 120s idle timer — called on any user activity while modal is open
  const resetIdleTimer = React.useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (transcriptFiredRef.current) return; // already logged this session
    idleTimerRef.current = setTimeout(() => {
      fireTranscriptWebhook();
    }, 120000); // 120s
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Start/stop idle timer based on modal open state + activity
  useEffect(() => {
    if (isOpen) {
      transcriptFiredRef.current = false; // reset guard on each new open
      resetIdleTimer();
    } else {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    }
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [isOpen, resetIdleTimer]);

  // Reset idle timer whenever chat activity happens
  useEffect(() => {
    if (isOpen && !transcriptFiredRef.current) {
      resetIdleTimer();
    }
  }, [messages, selectedAction, chatHistory, isOpen, resetIdleTimer]);

  // Request to close the modal. If there's any meaningful state to lose,
  // show a confirm overlay; otherwise close immediately.
  const requestClose = () => {
    const hasProgress =
      messages.length > 1 ||            // chatted or advanced past welcome
      selectedAction !== 'general' ||   // picked a category
      chatHistory.length > 0 ||         // talked to the bot
      escalationStage !== null;         // in middle of escalation flow
    if (hasProgress) {
      setShowCloseConfirm(true);
    } else {
      fireTranscriptWebhook();
      onClose();
    }
  };

  const confirmClose = () => {
    // Log transcript silently before wiping state
    fireTranscriptWebhook();
    // Wipe all state so the next open starts clean
    setShowCloseConfirm(false);
    setMessage('');
    setSelectedAction('general');
    setSelectedBillingOption(null);
    setSelectedUserHelpOption(null);
    setSelectedUser(null);
    setAssignedUsers([]);
    setReturnReason(null);
    setContactConfirmations({ inApp: false, phone: false, email: false });
    setContactedInApp(false);
    setContactedViaPhone(false);
    setContactedViaEmail(false);
    setPauseDuration(null);
    setRenewalDay(null);
    setShowPauseConfirmed(false);
    setPauseRenewalDate(null);
    setIsSubmitting(false);
    setIsChatMode(true);
    setChatHistory([]);
    setIsBotTyping(false);
    setUnhelpfulCount(0);
    setShowCategoriesPopover(false);
    setEscalationStage(null);
    setCapturedTranscript('');
    setMessages([{
      type: 'bot',
      text: 'Hi 👋 I\'m the Lauft support assistant. Choose a category below for common workflows, or just chat with me for anything else.',
    }]);
    onClose();
  };

  const cancelClose = () => {
    setShowCloseConfirm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ===== Escalation path — user is typing a note to include with the transcript =====
    if (escalationStage === 'adding-note') {
      const noteText = message.trim();
      if (!noteText) return;
      // Echo the user's note into the transcript view
      setMessages(prev => [...prev, { type: 'user', text: noteText }]);
      setMessage('');
      sendEscalationEmail(noteText);
      return;
    }

    // ===== Chatbot path — when in live-chat mode, route to support bot instead of ticket endpoint =====
    if (selectedAction === 'general' && isChatMode) {
      const userText = message.trim();
      if (!userText) return;

      // Echo user message into transcript
      setMessages(prev => [...prev, { type: 'user', text: userText }]);
      setMessage('');

      // Detect unhelpful patterns and bump counter
      let newUnhelpfulCount = unhelpfulCount;
      if (isUnhelpfulMessage(userText)) {
        newUnhelpfulCount = unhelpfulCount + 1;
        setUnhelpfulCount(newUnhelpfulCount);
      }

      // Update chat history (for OpenAI context on next turn)
      const nextHistory = [...chatHistory, { role: 'user', content: userText }];

      // Call the bot
      const result = await sendToSupportBot(userText);

      if (result.error) {
        setMessages(prev => [...prev, {
          type: 'bot',
          text: `Sorry — ${result.error} Try 'Email to team' to reach us directly.`
        }]);
        return;
      }

      // Append bot reply to messages + history
      setMessages(prev => [...prev, { type: 'bot', text: result.reply }]);
      setChatHistory([...nextHistory, { role: 'assistant', content: result.reply }]);

      // After 3 unhelpful signals in a session, proactively recommend Email to team
      if (newUnhelpfulCount >= 3) {
        setMessages(prev => [...prev, {
          type: 'bot',
          text: 'It sounds like this isn\'t hitting the mark. Use "Email to team" below and a human on our team will pick this up.'
        }]);
      }
      return;
    }

    console.log('🚀 handleSubmit called!', {
      selectedAction,
      selectedUserHelpOption,
      message: message,
      selectedUser: selectedUser?.name
    });
    
    // For return requests with unresponsive reason, message is optional (checkboxes are required)
    // For return requests with cannot_service or other, message is required
    // For other requests, message is required
    if (selectedUserHelpOption === 'return' && returnReason === 'unresponsive') {
      // Unresponsive: no message required, checkboxes are validated by button state
    } else if (!message.trim()) {
      console.log('⚠️ Blocked: No message provided');
      return;
    }
    
    console.log('✅ Proceeding with submission...');
    
    // Send email via SendGrid API through your backend
    // Backend endpoint should:
    // 1. Get the authenticated user's email from the session/token
    // 2. Use SendGrid API to send email to hello@lauft.app with:
    //    - From: user's email
    //    - To: hello@lauft.app
    //    - Subject: Support Request - [category]
    //    - Body: User's message + user info (name, email, agent ID)
    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/support/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          message: message,
          category: selectedAction || 'general',
          userHelpOption: selectedUserHelpOption || null,
          selectedUserId: selectedUser?._id || null,
          selectedUserName: selectedUser?.name || null,
          selectedUserEmail: selectedUser?.email || null,
          returnReason: returnReason || null,
          contactConfirmations: selectedUserHelpOption === 'return' && returnReason === 'unresponsive' ? contactConfirmations : null,
        })
      });
      
      console.log('📤 Support request response status:', response.status);
      console.log('📤 Response ok?:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📤 Success response data:', data);
        setMessages([
          ...messages,
          {
            type: 'bot',
            text: 'Thank you! Your message has been sent. We\'ll get back to you at your registered email address shortly.',
          }
        ]);
        setMessage('');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('📤 Error response:', response.status, errorData);
        setMessages([
          ...messages,
          {
            type: 'bot',
            text: 'Sorry, there was an error sending your message. Please try emailing us directly at hello@lauft.app',
          }
        ]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages([
        ...messages,
        {
          type: 'bot',
          text: 'Sorry, there was an error sending your message. Please try emailing us directly at hello@lauft.app',
        }
      ]);
    }
  };

  if (!isOpen) return null;

  const styles = getStyles(isLightMode);

  return (
    <>
      {/* Full-viewport dim backdrop — only while the cancellation-confirmed
          overlay is up, to pull focus to the support widget. Rendered as a
          sibling of the widget (not inside it) so it isn't clipped by the
          container's overflow:hidden, and sits just beneath the widget's
          z-index (1000) so the chat panel stays on top. */}
      {showCancelConfirmed && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            zIndex: 2147483646,
            animation: 'supportChatMsgIn 0.25s ease-out',
          }}
        />
      )}
    <div
      className="support-chat-widget"
      style={{
        ...styles.container,
        ...(showCancelConfirmed ? { zIndex: 2147483647 } : {}),
      }}
    >
      {/* Scoped animations, scrollbar, and typography overrides for the widget */}
      <style>{`
        @keyframes supportChatSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes supportChatMsgIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes supportChatDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30%           { transform: translateY(-4px); opacity: 1; }
        }
        .support-chat-widget .msg-fadein {
          animation: supportChatMsgIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .support-chat-widget .typing-dots {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 10px 14px;
        }
        .support-chat-widget .typing-dots span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: ${isLightMode ? '#9ca3af' : 'rgba(255,255,255,0.5)'};
          display: inline-block;
          animation: supportChatDot 1.2s infinite ease-in-out;
        }
        .support-chat-widget .typing-dots span:nth-child(2) { animation-delay: 0.15s; }
        .support-chat-widget .typing-dots span:nth-child(3) { animation-delay: 0.3s; }
        /* Custom thin scrollbar for the chat area */
        .support-chat-widget .chat-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .support-chat-widget .chat-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .support-chat-widget .chat-scroll::-webkit-scrollbar-thumb {
          background: ${isLightMode ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)'};
          border-radius: 4px;
        }
        .support-chat-widget .chat-scroll::-webkit-scrollbar-thumb:hover {
          background: ${isLightMode ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.22)'};
        }
        .support-chat-widget .chat-scroll {
          scrollbar-width: thin;
          scrollbar-color: ${isLightMode ? 'rgba(0,0,0,0.12) transparent' : 'rgba(255,255,255,0.12) transparent'};
        }
        /* Force white text on user chat bubbles, override any parent global CSS */
        div.support-chat-widget p.chat-user-message,
        div.support-chat-widget p.chat-user-message span,
        div.support-chat-widget .chat-user-message {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
        }
        /* Force header text to stay white on the gradient */
        div.support-chat-widget #support-title {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
        }
        div.support-chat-widget #support-subtitle {
          color: rgba(255, 255, 255, 0.65) !important;
          -webkit-text-fill-color: rgba(255, 255, 255, 0.65) !important;
        }
        div.support-chat-widget #support-close {
          color: #ffffff !important;
        }
        div.support-chat-widget #support-close svg {
          color: #ffffff !important;
          stroke: #ffffff !important;
        }
        /* Kill default browser focus outlines on all interactive elements inside the widget */
        div.support-chat-widget button,
        div.support-chat-widget input,
        div.support-chat-widget select,
        div.support-chat-widget textarea {
          -webkit-tap-highlight-color: transparent !important;
          -webkit-focus-ring-color: transparent !important;
        }
        div.support-chat-widget button:focus,
        div.support-chat-widget button:active,
        div.support-chat-widget button:focus-within,
        div.support-chat-widget input:focus,
        div.support-chat-widget button:focus-visible,
        div.support-chat-widget input:focus-visible {
          outline: none !important;
          outline-color: transparent !important;
        }
        /* Category pills + all widget buttons — no yellow tap/focus ring, ever */
        div.support-chat-widget button:focus,
        div.support-chat-widget button:active {
          box-shadow: none !important;
        }
        /* Force input focus state to use sage glow, not browser default yellow */
        div.support-chat-widget input:focus {
          border-color: rgba(122, 148, 113, 0.5) !important;
          box-shadow: 0 0 0 3px rgba(122, 148, 113, 0.12) !important;
        }
        /* Active send button keeps its own sage glow */
        div.support-chat-widget button.send-btn-active:focus,
        div.support-chat-widget button.send-btn-active:focus-visible {
          box-shadow: 0 2px 8px rgba(122, 148, 113, 0.35) !important;
        }
        .dashboard-container div.support-chat-widget .support-chat-header,
        .dashboard-container.light-mode div.support-chat-widget .support-chat-header,
        body div.support-chat-widget .support-chat-header,
        body.light-mode div.support-chat-widget .support-chat-header,
        div.support-chat-widget .support-chat-header {
          background: linear-gradient(135deg, #2a2d2a 0%, #3d4a3b 100%) !important;
          background-color: #2a2d2a !important;
        }
      `}</style>
      {/* Header */}
      <div className="support-chat-header" style={{
        padding: '18px 22px',
        borderBottom: isLightMode
          ? '1px solid rgba(0, 0, 0, 0.06)'
          : '1px solid rgba(255, 255, 255, 0.06)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
        background: isLightMode
          ? 'linear-gradient(135deg, #2a2d2a 0%, #3d4a3b 100%)'
          : 'transparent',
      }}>
       <div style={styles.headerContent}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: isLightMode ? 'rgba(255, 255, 255, 0.12)' : '#1a1a1a',
            border: isLightMode ? '1px solid rgba(255, 255, 255, 0.18)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}>
            <img
              src={darkIcon}
              alt="Lauft"
              style={{
                width: '28px',
                height: '28px',
                objectFit: 'contain',
              }}
            />
          </div>
          <div>
            <div id="support-title" style={{
              margin: 0,
              fontSize: '1rem',
              fontWeight: '600',
              color: '#ffffff'
            }}>Lauft Support</div>
            <div id="support-subtitle" style={{
              margin: 0,
              fontSize: '0.8125rem',
              color: 'rgba(255, 255, 255, 0.65)',
              marginTop: '2px',
              textAlign: 'left'
            }}>Help Bot</div>
          </div>
        </div>
        <button id="support-close" onClick={requestClose} style={{
          background: 'transparent',
          border: 'none',
          borderRadius: '8px',
          width: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#ffffff'
        }}>
          <X size={20} color="#ffffff" />
        </button>
      </div>

      {/* Close confirmation overlay — blocks interaction with the chat below */}
      {showCloseConfirm && (
        <div style={{
          position: 'absolute',
          inset: 0,
          marginTop: '72px', // sit below the header
          background: isLightMode ? 'rgba(245, 244, 240, 0.96)' : 'rgba(26, 26, 26, 0.96)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          animation: 'supportChatMsgIn 0.2s ease-out',
        }}>
          <div style={{
            background: isLightMode ? '#ffffff' : '#242424',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: isLightMode
              ? '0 8px 32px rgba(0, 0, 0, 0.12)'
              : '0 8px 32px rgba(0, 0, 0, 0.6)',
            border: `1px solid ${isLightMode ? 'rgba(122, 148, 113, 0.2)' : 'rgba(255, 255, 255, 0.08)'}`,
            maxWidth: '320px',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: '28px',
              marginBottom: '8px',
            }}>
              👋
            </div>
            <div style={{
              fontSize: '15px',
              fontWeight: 600,
              color: isLightMode ? '#2a2d2a' : '#f5f4f0',
              marginBottom: '6px',
            }}>
              Close this chat?
            </div>
            <div style={{
              fontSize: '13px',
              color: isLightMode ? '#6b7463' : 'rgba(245, 244, 240, 0.6)',
              lineHeight: '1.4',
              marginBottom: '18px',
            }}>
              Your current conversation will be cleared. Reopening starts a fresh chat.
            </div>
            <div style={{
              display: 'flex',
              gap: '8px',
            }}>
              <button
                type="button"
                onClick={cancelClose}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  background: isLightMode ? '#ffffff' : 'transparent',
                  border: `1px solid ${isLightMode ? 'rgba(122, 148, 113, 0.25)' : 'rgba(255, 255, 255, 0.18)'}`,
                  borderRadius: '8px',
                  color: isLightMode ? '#5d7456' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Keep chatting
              </button>
              <button
                type="button"
                onClick={confirmClose}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  background: '#7a9471',
                  border: '1px solid #7a9471',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: '0 1px 3px rgba(122, 148, 113, 0.3)',
                }}
              >
                Close chat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation-confirmed overlay — terminal state after a successful
          account cancellation. Covers the whole panel (including the header)
          and is not dismissable; the only action is to log out. */}
      {showCancelConfirmed && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: isLightMode ? 'rgba(245, 244, 240, 0.98)' : 'rgba(26, 26, 26, 0.98)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          zIndex: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          borderRadius: '20px',
          animation: 'supportChatMsgIn 0.2s ease-out',
        }}>
          <div style={{
            background: isLightMode ? '#ffffff' : '#242424',
            borderRadius: '16px',
            padding: '28px 24px',
            boxShadow: isLightMode
              ? '0 8px 32px rgba(0, 0, 0, 0.12)'
              : '0 8px 32px rgba(0, 0, 0, 0.6)',
            border: `1px solid ${isLightMode ? 'rgba(122, 148, 113, 0.2)' : 'rgba(255, 255, 255, 0.08)'}`,
            maxWidth: '320px',
            textAlign: 'center',
          }}>
            <div style={{
              width: '52px',
              height: '52px',
              borderRadius: '50%',
              background: '#7a9471',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 14px auto',
              boxShadow: '0 2px 10px rgba(122, 148, 113, 0.4)',
            }}>
              <span style={{ fontSize: '26px', color: '#ffffff', lineHeight: 1 }}>✓</span>
            </div>
            <div style={{
              fontSize: '16px',
              fontWeight: 700,
              color: isLightMode ? '#2a2d2a' : '#f5f4f0',
              marginBottom: '8px',
            }}>
              Account cancelled
            </div>
            <div style={{
              fontSize: '13px',
              color: isLightMode ? '#6b7463' : 'rgba(245, 244, 240, 0.65)',
              lineHeight: '1.5',
              marginBottom: '20px',
            }}>
              Your cancellation has been submitted and your account has been deactivated. You've been removed from all territories, no further billing will occur, and and a confirmation email is on its way.
            </div>
            <button
              type="button"
              onClick={handleCancelLogout}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: '#7a9471',
                border: '1px solid #7a9471',
                borderRadius: '10px',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                boxShadow: '0 1px 3px rgba(122, 148, 113, 0.3)',
              }}
            >
              Log out
            </button>
          </div>
        </div>
      )}

      {/* Pause confirmed overlay — success state after a pause submits. Unlike
          the cancel overlay this one is dismissable: click the X or the backdrop
          to return to the chat, or "Close chat" to close the whole modal. */}
      {showPauseConfirmed && (
        <div
          onClick={() => setShowPauseConfirmed(false)}
          style={{
            position: 'absolute',
            inset: 0,
            background: isLightMode ? 'rgba(245, 244, 240, 0.98)' : 'rgba(26, 26, 26, 0.98)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            borderRadius: '20px',
            animation: 'supportChatMsgIn 0.2s ease-out',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              background: isLightMode ? '#ffffff' : '#242424',
              borderRadius: '16px',
              padding: '28px 24px',
              boxShadow: isLightMode
                ? '0 8px 32px rgba(0, 0, 0, 0.12)'
                : '0 8px 32px rgba(0, 0, 0, 0.6)',
              border: `1px solid ${isLightMode ? 'rgba(122, 148, 113, 0.2)' : 'rgba(255, 255, 255, 0.08)'}`,
              maxWidth: '320px',
              textAlign: 'center',
            }}
          >
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setShowPauseConfirmed(false)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                width: '26px',
                height: '26px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: isLightMode ? '#9aa39a' : 'rgba(245, 244, 240, 0.55)',
                fontSize: '18px',
                lineHeight: 1,
                fontFamily: 'inherit',
              }}
            >
              ×
            </button>
            <div style={{
              width: '52px',
              height: '52px',
              borderRadius: '50%',
              background: '#7a9471',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 14px auto',
              boxShadow: '0 2px 10px rgba(122, 148, 113, 0.4)',
            }}>
              <span style={{ fontSize: '26px', color: '#ffffff', lineHeight: 1 }}>✓</span>
            </div>
            <div style={{
              fontSize: '16px',
              fontWeight: 700,
              color: isLightMode ? '#2a2d2a' : '#f5f4f0',
              marginBottom: '8px',
            }}>
              Account paused
            </div>
            <div style={{
              fontSize: '13px',
              color: isLightMode ? '#6b7463' : 'rgba(245, 244, 240, 0.65)',
              lineHeight: '1.5',
              marginBottom: '16px',
            }}>
              Your pause is in. No billing or point renewals will occur until your account automatically resumes — you can still claim users, message users, and use points in the meantime.
            </div>
            {pauseRenewalDate && (
              <div style={{
                padding: '10px 14px',
                marginBottom: '20px',
                background: '#fef6e7',
                border: '1px solid #f5d98b',
                borderRadius: '10px',
              }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#92600a', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                  Next renewal date
                </div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#92600a' }}>
                  {pauseRenewalDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={confirmClose}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: '#7a9471',
                border: '1px solid #7a9471',
                borderRadius: '10px',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                boxShadow: '0 1px 3px rgba(122, 148, 113, 0.3)',
              }}
            >
              Close chat
            </button>
          </div>
        </div>
      )}

      {/* Chat Area */}
      <div ref={chatAreaRef} className="chat-scroll" style={styles.chatArea}>
        {/* Initial Welcome Message */}
        <div style={styles.welcomeMessage}>
          <div style={styles.botAvatar}>
            <HelpCircle size={18} color="#ffffff" />
          </div>
          <div style={styles.messageBubble}>
            <p style={styles.messageText}>
              Hi 👋 Start chatting below or pick a category to jump in.
            </p>
          </div>
        </div>

        {/* Quick Actions as Bubbles */}
        <div style={styles.bubbleContainer}>
          <button 
            className={selectedAction === 'userhelp' ? 'bubble-button-selected' : ''}
            style={{
              ...styles.bubbleButton,
              ...(selectedAction === 'userhelp' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
            }}
            onClick={() => handleQuickAction('userhelp')}
          >
            👤 Help with a User
          </button>
          <button 
            className={selectedAction === 'billing' ? 'bubble-button-selected' : ''}
            style={{
              ...styles.bubbleButton,
              ...(selectedAction === 'billing' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
            }}
            onClick={() => handleQuickAction('billing')}
          >
            💳 Billing
          </button>
          <button 
            className={selectedAction === 'feature' ? 'bubble-button-selected' : ''}
            style={{
              ...styles.bubbleButton,
              ...(selectedAction === 'feature' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
            }}
            onClick={() => handleQuickAction('feature')}
          >
            💡 Feature Request
          </button>
          <button 
            className={selectedAction === 'bug' ? 'bubble-button-selected' : ''}
            style={{
              ...styles.bubbleButton,
              ...(selectedAction === 'bug' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
            }}
            onClick={() => handleQuickAction('bug')}
          >
            🐛 Report a Bug
          </button>
          <button 
            className={selectedAction === 'call' ? 'bubble-button-selected' : ''}
            style={{
              ...styles.bubbleButton,
              ...(selectedAction === 'call' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
            }}
            onClick={() => handleQuickAction('call')}
          >
            🗓️ Schedule a Call
          </button>
        </div>

        {/* Additional Messages (appear after bubble click) */}
        {messages.length > 1 && messages.slice(1).map((msg, index) => (
          <React.Fragment key={index + 1}>
            {msg.type === 'user' ? (
              /* User message: right-aligned, no avatar, accent color */
              <div className="msg-fadein" style={styles.userMessage}>
                <div style={{
                  backgroundColor: '#7a9471',
                  borderRadius: '16px 16px 4px 16px',
                  padding: '10px 14px',
                  maxWidth: '280px',
                  alignSelf: 'flex-start',
                  flexShrink: 0,
                  boxShadow: '0 1px 3px rgba(122, 148, 113, 0.3)',
                }}>
                  <p className="chat-user-message" style={{
                    margin: 0,
                    fontSize: '0.9375rem',
                    color: '#ffffff',
                    lineHeight: '1.5',
                    textAlign: 'left',
                    fontWeight: 500,
                    whiteSpace: 'pre-line',
                    WebkitTextFillColor: '#ffffff',
                  }}>
                    <span style={{
                      color: '#ffffff',
                      WebkitTextFillColor: '#ffffff',
                    }}>
                      {msg.text}
                    </span>
                  </p>
                </div>
              </div>
            ) : (
              /* Bot message: left-aligned, avatar, neutral fill */
              <div className="msg-fadein" style={styles.welcomeMessage}>
                <div style={styles.botAvatar}>
                  <HelpCircle size={18} color="#ffffff" />
                </div>
                <div style={styles.messageBubble}>
                  <p style={{...styles.messageText, whiteSpace: 'pre-line'}}>
                    {msg.text}
                  </p>
                </div>
              </div>
            )}
            
            {/* Show Billing Options after "What would you like to do with your account?" */}
            {index === 0 && selectedAction === 'billing' && msg.text === 'What would you like to do with your account?' && (
              <div style={styles.bubbleContainer}>
                {accountPaused && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '10px 14px',
                      marginBottom: '4px',
                      background: '#fef6e7',
                      border: '1px solid #f5d98b',
                      borderRadius: '10px',
                      color: '#92600a',
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    <span style={{ fontSize: '1rem', flexShrink: 0 }}>⏸️</span>
                    <span>
                      Your account is already on pause{pauseEndsAt ? ` until ${new Date(pauseEndsAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}` : ''}.
                    </span>
                  </div>
                )}
                <button 
                  className={selectedBillingOption === 'change-renewal' ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(selectedBillingOption === 'change-renewal' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {}),
                    ...(accountPaused ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                  }}
                  onClick={() => handleBillingOption('change-renewal')}
                  aria-disabled={accountPaused}
                  title={pausedOptionTooltip}
                >
                  📅 Change Renewal Date
                </button>
                <button 
                  className={selectedBillingOption === 'pause' ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(selectedBillingOption === 'pause' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {}),
                    ...(accountPaused ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                  }}
                  onClick={() => handleBillingOption('pause')}
                  aria-disabled={accountPaused}
                  title={pausedOptionTooltip}
                >
                  ⏸️ Pause Account
                </button>
                <button 
                  className={selectedBillingOption === 'cancel' ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(selectedBillingOption === 'cancel' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
                  }}
                  onClick={() => handleBillingOption('cancel')}
                >
                  ❌ Cancel Account
                </button>
              </div>
            )}
            
            {/* Show User Help Options after "What would you like to do with a user?" */}
            {index === 0 && selectedAction === 'userhelp' && msg.text === 'What would you like to do with a user?' && (
              <div style={styles.bubbleContainer}>
                <button 
                  className={selectedUserHelpOption === 'return' ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(selectedUserHelpOption === 'return' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
                  }}
                  onClick={() => handleUserHelpOption('return')}
                >
                  ↩️ Return a User
                </button>
                <button 
                  className={selectedUserHelpOption === 'reassign' ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(selectedUserHelpOption === 'reassign' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
                  }}
                  onClick={() => handleUserHelpOption('reassign')}
                >
                  🔄 Reassign a User
                </button>
                <button 
                  className={selectedUserHelpOption === 'report' ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(selectedUserHelpOption === 'report' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
                  }}
                  onClick={() => handleUserHelpOption('report')}
                >
                  🚨 Report a User
                </button>
              </div>
            )}
            
            {/* Show User Dropdown after selecting a user help option */}
            {(() => {
              const shouldShow = index === 1 && selectedAction === 'userhelp' && selectedUserHelpOption && msg.text.includes('Please select the user');
              console.log('🔍 Dropdown check:', {
                index,
                msgText: msg.text,
                selectedAction,
                selectedUserHelpOption,
                textIncludes: msg.text.includes('Please select the user'),
                shouldShow,
                assignedUsers: assignedUsers.length,
                messagesLength: messages.length
              });
              return shouldShow;
            })() && (
              <div style={{
                marginTop: '12px',
                paddingLeft: '44px',
                paddingRight: '20px',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                {loadingUsers ? (
                    <div style={{padding: '12px', textAlign: 'center', color: isLightMode ? '#6b7280' : '#ffffff', fontSize: '14px'}}>
                      Loading users...
                    </div>
                  ) : assignedUsers.length === 0 ? (
                    <div style={{padding: '12px', textAlign: 'center', color: isLightMode ? '#6b7280' : '#ffffff', fontSize: '14px'}}>
                      No assigned users found
                    </div>
                  ) : (
                    <select
                      value={selectedUser?._id || ''}
                      onChange={(e) => {
                        const user = assignedUsers.find(u => u._id === e.target.value);
                        if (user) {
                          console.log('USER SELECTED:', user);
                          handleUserSelect(user);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: isLightMode ? '#ffffff' : '#2a2a2a',
                        border: isLightMode ? '1px solid #e5e7eb' : '1px solid #444444',
                        borderRadius: '12px',
                        fontSize: '14px',
                        color: isLightMode ? '#1f2937' : '#ffffff',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#818cf8';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(129, 140, 248, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = isLightMode ? '#e5e7eb' : '#444444';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <option value="" disabled>Select a user...</option>
                      {assignedUsers.map(user => (
                        <option key={user._id} value={user._id}>
                          {user.name} - {user.email}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
            )}
            
            {/* Show Return Reason Selection after selecting user */}
            {index === 3 && selectedAction === 'userhelp' && selectedUserHelpOption === 'return' && selectedUser && 
             msg.text.includes('Why are you returning this user?') && (
              <div style={styles.bubbleContainer}>
                <button 
                  className={returnReason === 'unresponsive' ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(returnReason === 'unresponsive' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
                  }}
                  onClick={() => handleReturnReason('unresponsive')}
                >
                  🔇 User is Unresponsive
                </button>
                <button 
                  className={returnReason === 'cannot_service' ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(returnReason === 'cannot_service' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
                  }}
                  onClick={() => handleReturnReason('cannot_service')}
                >
                  ⚠️ I Cannot Service User
                </button>
                <button 
                  className={returnReason === 'other' ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(returnReason === 'other' ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
                  }}
                  onClick={() => handleReturnReason('other')}
                >
                  💬 Other
                </button>
              </div>
            )}
            
            {/* Show Contact Confirmation Checkboxes for "return" after selecting user */}
            {index === 4 && selectedAction === 'userhelp' && selectedUserHelpOption === 'return' && selectedUser && returnReason === 'unresponsive' &&
             msg.text.includes('Please confirm you have contacted') && (
              <div style={{
                ...styles.bubbleContainer, 
                flexDirection: 'column', 
                gap: '16px', 
                alignItems: 'flex-start',
                padding: '16px',
                backgroundColor: isLightMode ? '#f9fafb' : 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  color: isLightMode ? '#1f2937' : 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px'
                }}>
                  <input
                    type="checkbox"
                    checked={contactConfirmations.inApp}
                    onChange={() => handleContactConfirmation('inApp')}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#818cf8'
                    }}
                  />
                  <span>📱 Contacted via In-App Messaging</span>
                </label>
                
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  color: isLightMode ? '#1f2937' : 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px'
                }}>
                  <input
                    type="checkbox"
                    checked={contactConfirmations.phone}
                    onChange={() => handleContactConfirmation('phone')}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#818cf8'
                    }}
                  />
                  <span>📞 Contacted via Phone</span>
                </label>
                
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  color: isLightMode ? '#1f2937' : 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px'
                }}>
                  <input
                    type="checkbox"
                    checked={contactConfirmations.email}
                    onChange={() => handleContactConfirmation('email')}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#818cf8'
                    }}
                  />
                  <span>📧 Contacted via Email</span>
                </label>
              </div>
            )}
            
            {/* Show Pause Duration Options after "How long would you like to pause your account?" */}
            {index === 1 && selectedBillingOption === 'pause' && msg.text === 'How long would you like to pause your account?' && (
              <div style={{...styles.bubbleContainer, flexDirection: 'column', gap: '12px', alignItems: 'flex-start'}}>
                <button 
                  className={pauseDuration === 30 ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(pauseDuration === 30 ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
                  }}
                  onClick={() => handlePauseDuration(30)}
                >
                  30 Days
                </button>
                <button 
                  className={pauseDuration === 45 ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(pauseDuration === 45 ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
                  }}
                  onClick={() => handlePauseDuration(45)}
                >
                  45 Days
                </button>
                <button 
                  className={pauseDuration === 60 ? 'bubble-button-selected' : ''}
                  style={{
                    ...styles.bubbleButton,
                    ...(pauseDuration === 60 ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {})
                  }}
                  onClick={() => handlePauseDuration(60)}
                >
                  60 Days
                </button>
              </div>
            )}
            
            {/* Show Renewal Day Options after "What day of the month would you like your renewal to occur? (1-31)" */}
            {index === 1 && selectedBillingOption === 'change-renewal' && msg.text === 'What day of the month would you like your renewal to occur? (1-31)' && (
              <div style={{...styles.bubbleContainer, flexWrap: 'wrap', gap: '8px'}}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                  <button 
                    key={day}
                    className={renewalDay === day ? 'bubble-button-selected' : ''}
                    style={{
                      ...styles.bubbleButton,
                      ...(renewalDay === day ? {...styles.bubbleButtonSelected, color: '#ffffff'} : {}),
                      minWidth: '50px'
                    }}
                    onClick={() => handleRenewalDay(day)}
                  >
                    {day}
                  </button>
                ))}
              </div>
            )}
          </React.Fragment>
        ))}

        {/* Escalation Y/N buttons — shown when user has clicked "Email to team" and we're asking if they want to add a note */}
        {escalationStage === 'asking' && (
          <div style={{...styles.bubbleContainer, flexWrap: 'wrap', gap: '8px'}}>
            <button
              type="button"
              style={styles.bubbleButton}
              onClick={handleEscalationAddNote}
            >
              ✏️ Yes, add a note
            </button>
            <button
              type="button"
              style={styles.bubbleButton}
              onClick={handleEscalationSendNow}
            >
              ✅ No, send it now
            </button>
          </div>
        )}

        {escalationStage === 'sending' && (
          <div style={{
            padding: '8px 16px',
            fontSize: '12px',
            color: isLightMode ? '#888' : 'rgba(255,255,255,0.5)',
            fontStyle: 'italic',
          }}>
            Sending…
          </div>
        )}

        {escalationStage === 'sent' && (
          <div style={{...styles.bubbleContainer, flexWrap: 'wrap', gap: '8px'}}>
            <button
              type="button"
              style={styles.bubbleButton}
              onClick={() => {
                // Reset back to fresh chat mode
                setEscalationStage(null);
                setCapturedTranscript('');
                setChatHistory([]);
                setUnhelpfulCount(0);
                setMessage('');
                setMessages([
                  {
                    type: 'bot',
                    text: 'Back at it! What else can I help you with?'
                  }
                ]);
              }}
            >
              💬 Start a new chat
            </button>
          </div>
        )}
      </div>

      {/* Send-to-team banner + typing indicator (chat mode only, not during escalation) */}
      {isChatMode && escalationStage === null && (
        <>
          {isBotTyping && (
            <div style={{ padding: '0 16px 8px 16px' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={styles.botAvatar}>
                  <HelpCircle size={18} color="#ffffff" />
                </div>
                <div style={{
                  backgroundColor: isLightMode ? '#f3f4f6' : 'rgba(255, 255, 255, 0.08)',
                  borderRadius: '16px 16px 16px 4px',
                }}>
                  <span className="typing-dots" aria-label="Assistant is typing">
                    <span></span><span></span><span></span>
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Categories popover (anchored above the button row) */}
          {showCategoriesPopover && (
            <div style={{
              position: 'relative',
              margin: '0 16px',
            }}>
              <div style={{
                position: 'absolute',
                bottom: '4px',
                left: 0,
                right: 0,
                background: isLightMode ? '#ffffff' : '#1a1a1a',
                border: `1px solid ${isLightMode ? '#e5e7eb' : 'rgba(255,255,255,0.15)'}`,
                borderRadius: '10px',
                padding: '10px',
                boxShadow: isLightMode ? '0 4px 12px rgba(0,0,0,0.08)' : '0 4px 12px rgba(0,0,0,0.4)',
                zIndex: 10,
              }}>
                <div style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: isLightMode ? '#6b7280' : 'rgba(255,255,255,0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '8px',
                }}>
                  Jump to a category
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {[
                    { key: 'userhelp', label: '👤 User Help' },
                    { key: 'billing',  label: '💳 Billing' },
                    { key: 'feature',  label: '💡 Feature' },
                    { key: 'bug',      label: '🐛 Bug' },
                    { key: 'call',     label: '🗓️ Call' },
                  ].map(cat => (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => {
                        setShowCategoriesPopover(false);
                        handleQuickAction(cat.key);
                      }}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        borderRadius: '999px',
                        border: `1px solid ${isLightMode ? '#e5e7eb' : 'rgba(255,255,255,0.15)'}`,
                        background: isLightMode ? '#f9fafb' : 'rgba(255,255,255,0.04)',
                        color: isLightMode ? '#1a1a1a' : '#ffffff',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Two-button action row: Categories + Email to team */}
          <div style={{
            display: 'flex',
            gap: '8px',
            margin: '8px 16px',
          }}>
            <button
              type="button"
              onClick={() => setShowCategoriesPopover(prev => !prev)}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: showCategoriesPopover
                  ? (isLightMode ? 'rgba(122, 148, 113, 0.12)' : 'rgba(122, 148, 113, 0.18)')
                  : (isLightMode ? '#ffffff' : 'transparent'),
                border: `1px solid ${showCategoriesPopover
                  ? (isLightMode ? 'rgba(122, 148, 113, 0.4)' : 'rgba(122, 148, 113, 0.5)')
                  : (isLightMode ? 'rgba(122, 148, 113, 0.25)' : 'rgba(255,255,255,0.18)')}`,
                borderRadius: '8px',
                color: showCategoriesPopover
                  ? (isLightMode ? '#5d7456' : '#a3bd9c')
                  : (isLightMode ? '#5d7456' : 'rgba(255,255,255,0.7)'),
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                letterSpacing: '0.02em',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
              title="Jump to a specific category"
            >
              {showCategoriesPopover ? '✕ Close' : 'Categories ▴'}
            </button>
            <button
              type="button"
              onClick={handleSendToTeam}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: isLightMode ? '#ffffff' : 'transparent',
                border: `1px solid ${isLightMode ? 'rgba(122, 148, 113, 0.25)' : 'rgba(255,255,255,0.18)'}`,
                borderRadius: '8px',
                color: isLightMode ? '#5d7456' : 'rgba(255,255,255,0.7)',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                letterSpacing: '0.02em',
                fontFamily: 'inherit',
              }}
              title="Email this conversation to our team"
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <Mail size={13} strokeWidth={2.25} />
                Email to team
              </span>
            </button>
          </div>
        </>
      )}

      {/* Back to chat bar — visible only when in a structured category flow (replaces the two-chip row) */}
      {!isChatMode && (
        <button
          type="button"
          onClick={() => handleQuickAction('general')}
          style={{
            display: 'block',
            width: 'calc(100% - 32px)',
            margin: '8px 16px',
            padding: '8px 12px',
            background: isLightMode ? '#ffffff' : 'transparent',
            border: `1px solid ${isLightMode ? 'rgba(122, 148, 113, 0.25)' : 'rgba(255,255,255,0.18)'}`,
            borderRadius: '8px',
            color: isLightMode ? '#5d7456' : 'rgba(255,255,255,0.7)',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            letterSpacing: '0.02em',
            fontFamily: 'inherit',
          }}
          title="Return to chat with the support assistant"
        >
          ← Back to chat
        </button>
      )}

      {/* Input Area or Submit Button */}
      {selectedAction === 'billing' && messages.length === 2 ? (
        // Don't show submit button yet - wait for sub-options to be selected
        null
      ) : selectedAction === 'userhelp' && !selectedUser && !selectedUserHelpOption ? (
        // Userhelp: user hasn't picked a sub-option yet — hide input
        null
      ) : selectedAction === 'userhelp' && !selectedUser ? (
        // Userhelp: sub-option picked but user not selected yet — hide input
        null
      ) : selectedAction === 'billing' && selectedBillingOption === 'change-renewal' && messages.length === 4 ? (
        // Show "Submit" button for change-renewal with day selected
        <form onSubmit={handleBillingSubmit} style={styles.inputArea}>
          <button 
            type="submit"
            style={{
              ...styles.submitButton,
              ...(renewalDay && !isSubmitting ? styles.submitButtonActive : {})
            }}
            disabled={!renewalDay || isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Change Renewal Date'}
          </button>
        </form>
      ) : selectedAction === 'billing' && selectedBillingOption === 'pause' && messages.length === 6 ? (
        // Show "Pause Account" button for pause with duration selected
        <form onSubmit={handleBillingSubmit} style={styles.inputArea}>
          <button 
            type="submit"
            style={{
              ...styles.submitButton,
              ...(pauseDuration && !isSubmitting ? styles.submitButtonActive : {})
            }}
            disabled={!pauseDuration || isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Pause Account'}
          </button>
        </form>
      ) : selectedAction === 'billing' && selectedBillingOption === 'cancel' && messages.length === 4 ? (
        // Show "Cancel Account" button for cancel option
        <form onSubmit={handleBillingSubmit} style={styles.inputArea}>
          <button 
            type="submit"
            style={{
              ...styles.submitButton,
              ...(isSubmitting ? {} : styles.submitButtonActive)
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Cancel Account'}
          </button>
        </form>
      ) : selectedAction === 'billing' && messages.length > 2 && messages.length !== 6 && messages.length !== 4 && messages.length !== 3 ? (
        // Hide input after billing submission (but not during pause flow)
        null
      ) : selectedAction === 'userhelp' && selectedUserHelpOption === 'return' && selectedUser && returnReason === 'unresponsive' && messages.length === 6 ? (
        // Show "Return User" button for unresponsive after all checkboxes are checked
        <form onSubmit={handleSubmit} style={styles.inputArea}>
          <button 
            type="submit"
            style={{
              ...styles.submitButton,
              ...(contactConfirmations.inApp && contactConfirmations.phone && contactConfirmations.email && !isSubmitting ? styles.submitButtonActive : {})
            }}
            disabled={!contactConfirmations.inApp || !contactConfirmations.phone || !contactConfirmations.email || isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Submit Return Request'}
          </button>
        </form>
      ) : selectedAction === 'userhelp' && selectedUserHelpOption === 'return' && selectedUser && (returnReason === 'cannot_service' || returnReason === 'other') && messages.length === 6 ? (
        // Show text input for cannot_service or other return reasons
        <form onSubmit={handleSubmit} style={styles.inputArea}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Explain why..."
            style={styles.input}
          />
          <button 
            type="submit" 
            style={{
              ...styles.sendButton,
              ...(message.trim() ? styles.sendButtonActive : {})
            }}
            disabled={!message.trim()}
          >
            <Send size={18} />
          </button>
        </form>
      ) : selectedAction === 'userhelp' && (selectedUserHelpOption === 'reassign' || selectedUserHelpOption === 'report') && selectedUser && messages.length === 4 ? (
        // Show text input for reassign and report after user selection
        <form onSubmit={handleSubmit} style={styles.inputArea}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            style={styles.input}
          />
          <button 
            type="submit" 
            style={{
              ...styles.sendButton,
              ...(message.trim() ? styles.sendButtonActive : {})
            }}
            disabled={!message.trim()}
          >
            <Send size={18} />
          </button>
        </form>
      ) : selectedAction === 'userhelp' && messages.length > 4 ? (
        // Hide input after userhelp submission
        null
      ) : (
        // Normal input for other actions
        <form onSubmit={handleSubmit} style={styles.inputArea}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              escalationStage === 'adding-note'
                ? 'Type your note...'
                : escalationStage === 'sending' || escalationStage === 'sent'
                  ? ''
                  : 'Type your message...'
            }
            style={styles.input}
            disabled={escalationStage === 'sending' || escalationStage === 'sent'}
          />
          <button 
            type="submit" 
            style={{
              ...styles.sendButton,
              ...(message.trim() ? styles.sendButtonActive : {})
            }}
            disabled={!message.trim()}
          >
            <Send size={18} />
          </button>
        </form>
      )}
    </div>
    </>
  );
};

const getStyles = (isLightMode) => ({
  container: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '400px',
    height: '600px',
    backgroundColor: isLightMode ? '#ffffff' : '#1a1a1a',
    borderRadius: '20px',
    boxShadow: isLightMode
      ? '0 20px 60px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.08)'
      : '0 20px 60px rgba(0, 0, 0, 0.65), 0 0 0 1px rgba(255, 255, 255, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1000,
    animation: 'supportChatSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    overflow: 'hidden',
  },
  header: {
    padding: '18px 22px',
    borderBottom: isLightMode
      ? '1px solid rgba(0, 0, 0, 0.06)'
      : '1px solid rgba(255, 255, 255, 0.06)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  iconCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '600',
    color: '#ffffff', // Always white
  },
  subtitle: {
    margin: 0,
    fontSize: '0.8125rem',
    color: 'rgba(255, 255, 255, 0.6)', // Always white with opacity
    marginTop: '2px',
    textAlign: 'left',
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff', // Always white
    transition: 'background-color 0.2s',
  },
  chatArea: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  welcomeMessage: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  },
  botAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#2a2d2a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  messageBubble: {
    backgroundColor: isLightMode ? '#f5f4f0' : 'rgba(245, 244, 240, 0.08)',
    borderRadius: '16px 16px 16px 4px',
    padding: '10px 14px',
    maxWidth: '280px',
    alignSelf: 'flex-start',
    flexShrink: 0,
  },
  messageText: {
    margin: 0,
    fontSize: '0.9375rem',
    color: isLightMode ? '#2a2d2a' : '#f5f4f0',
    lineHeight: '1.5',
    textAlign: 'left',
  },
  userMessage: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
  },
  userMessageBubble: {
    backgroundColor: '#7a9471',
    borderRadius: '16px 16px 4px 16px',
    padding: '10px 14px',
    maxWidth: '280px',
    alignSelf: 'flex-start',
    flexShrink: 0,
    boxShadow: '0 1px 3px rgba(122, 148, 113, 0.3)',
  },
  userMessageText: {
    margin: 0,
    fontSize: '0.9375rem',
    color: '#ffffff',
    lineHeight: '1.5',
    textAlign: 'left',
    fontWeight: 500,
  },
  bubbleContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
    paddingLeft: '44px', // Align with message text (avatar width + gap)
  },
  bubbleButton: {
    padding: '10px 18px',
    backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.08)',
    border: isLightMode ? '1px solid #e5e7eb' : '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '20px',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: isLightMode ? '#1a1a1a' : '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
    boxShadow: isLightMode ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none',
  },
  bubbleButtonSelected: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #1a1a1a',
    borderColor: '#1a1a1a',
    color: '#ffffff', // Always white when selected
    fontWeight: '600',
  },
  bubbleButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  inputArea: {
    padding: '16px 20px 20px 20px',
    borderTop: isLightMode
      ? '1px solid rgba(122, 148, 113, 0.15)'
      : '1px solid rgba(255, 255, 255, 0.06)',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    backgroundColor: isLightMode ? '#f4f6f2' : '#0f0f0f',
  },
  input: {
    flex: 1,
    padding: '14px 18px',
    backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.08)',
    border: isLightMode ? '1px solid rgba(122, 148, 113, 0.25)' : '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '24px',
    fontSize: '0.9375rem',
    color: isLightMode ? '#2a2d2a' : '#ffffff',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  sendButton: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: isLightMode ? '#e5e7eb' : 'rgba(255, 255, 255, 0.1)',
    color: isLightMode ? '#9ca3af' : 'rgba(255, 255, 255, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    flexShrink: 0,
  },
  sendButtonActive: {
    backgroundColor: '#7a9471',
    color: '#ffffff',
    boxShadow: '0 2px 8px rgba(122, 148, 113, 0.35)',
  },
  submitButton: {
    width: '100%',
    padding: '14px',
    borderRadius: '24px',
    border: 'none',
    backgroundColor: isLightMode ? '#e5e7eb' : 'rgba(255, 255, 255, 0.1)',
    color: isLightMode ? '#9ca3af' : 'rgba(255, 255, 255, 0.3)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitButtonActive: {
    backgroundColor: '#7a9471',
    color: '#ffffff',
    boxShadow: '0 2px 8px rgba(122, 148, 113, 0.35)',
  },
});

export default SupportChat;